import { Camera, ChevronRight, ListIcon, MessageSquare, Mic } from "lucide-react";
import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatearAjuste, formatearCifra, formatearMonto, formatearVidaUtil } from "@/lib/montos";
import { cn } from "@/lib/utils";

import { formularioDesdeElItemDeTerreno } from "./construcciones";
import type { FilaDelCuadro, ModoDelTerreno } from "./cuadroDeConstrucciones";
import { pluralizar } from "./registroDeTerreno";

/**
 * La casilla de SELECCIÓN marca en tinta oscura, como en el diseño, y no en
 * verde: el verde es de «Valid.» —un dato revisado— y las dos se leían igual.
 * `foreground` invierte con el tema y el tilde (`background`) con él.
 */
const SELECCION_OSCURA =
  "data-[state=checked]:bg-foreground data-[state=checked]:border-foreground data-[state=checked]:text-background dark:data-[state=checked]:bg-foreground";

interface TablaDeConstruccionesProps {
  filas: FilaDelCuadro[];
  modo: ModoDelTerreno;
  /** Quién salió a terreno: da el nombre en la insignia TERRENO. */
  encargado: string | null;
  seleccion: ReadonlySet<string>;
  todasSeleccionadas: boolean;
  ocupada: boolean;
  onAlternarSeleccion: (fila: FilaDelCuadro) => void;
  onAlternarTodas: () => void;
  onAbrir: (fila: FilaDelCuadro) => void;
  onValidar: (fila: FilaDelCuadro, validada: boolean) => void;
}

/** La insignia que marca que la construcción nació en la App de terreno. */
export function InsigniaTerreno({ titulo }: { titulo: string }) {
  return (
    <span
      title={titulo}
      className="bg-info-soft text-info-soft-foreground border-info-dot/30 flex-none rounded-sm border px-1.5 py-px text-[0.59375rem] font-extrabold tracking-[0.04em]"
    >
      TERRENO
    </span>
  );
}

function Cifra({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("font-mono text-xs font-semibold", className)}>{children}</span>;
}

/** Un contador de la celda de terreno: ícono y cifra, con su nombre accesible. */
function Contador({
  etiqueta,
  cantidad,
  icono,
}: {
  etiqueta: string;
  cantidad: number;
  icono: ReactNode;
}) {
  if (cantidad === 0) return null;

  return (
    <span aria-label={etiqueta} className="text-ink-muted inline-flex items-center gap-1">
      {icono}
      <span className="font-mono text-[0.6875rem] font-bold">{cantidad}</span>
    </span>
  );
}

/** Lo que la fila trae de la visita: sus registros, sus notas, o por qué no hay. */
function CeldaDeTerreno({ fila, modo }: { fila: FilaDelCuadro; modo: ModoDelTerreno }) {
  const { registros, notas } = fila;
  const sinRegistros = modo === "pendiente" || registros.total === 0;
  const detalleDeLasNotas = notas.map((n) => `${n.etiqueta}: ${n.texto}`).join("\n");

  return (
    <div className="flex items-center gap-2.5">
      {sinRegistros ? (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[0.6875rem] font-bold",
            modo === "pendiente"
              ? "bg-warning-soft text-warning-soft-foreground"
              : "text-ink-muted",
          )}
        >
          <span
            className={cn(
              "size-[0.4375rem] flex-none rounded-full",
              modo === "pendiente" ? "bg-warning-dot" : "bg-border",
            )}
          />
          {modo === "pendiente" ? "Visita pendiente" : "Sin registros"}
        </span>
      ) : null}

      {modo === "sincronizado" ? (
        <span className="inline-flex items-center gap-2.5">
          <Contador
            etiqueta={pluralizar(registros.fotos, "foto", "fotos")}
            cantidad={registros.fotos}
            icono={<Camera className="size-3" aria-hidden="true" />}
          />
          <Contador
            etiqueta={pluralizar(registros.audios, "audio", "audios")}
            cantidad={registros.audios}
            icono={<Mic className="size-3" aria-hidden="true" />}
          />
          <Contador
            etiqueta={pluralizar(registros.textos, "texto", "textos")}
            cantidad={registros.textos}
            icono={<ListIcon className="size-3" aria-hidden="true" />}
          />
          {notas.length > 0 ? (
            <span
              title={detalleDeLasNotas}
              aria-label={
                notas.length === 1
                  ? "1 nota de terreno sobre un dato"
                  : `${notas.length} notas de terreno sobre los datos`
              }
              className="bg-warning-soft text-warning-soft-foreground inline-flex items-center gap-1 rounded-md px-1.5 py-0.5"
            >
              <MessageSquare className="size-3" aria-hidden="true" />
              <span className="font-mono text-[0.6875rem] font-bold">{notas.length}</span>
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/**
 * La tabla de la lista: una fila por construcción, hecha con el `Table` del DS.
 *
 * LA FILA ENTERA ABRE EL MODAL, y el tipo es además un botón: un `onClick` en
 * un `<tr>` no lo alcanza el teclado. Las dos casillas frenan la propagación
 * —marcar no puede abrir el detalle—.
 *
 * UNA CONSTRUCCIÓN DE TERRENO SIN GUARDAR no es una fila del cuadro todavía:
 * se puede abrir y guardar desde el modal, pero no validar ni seleccionar
 * —no hay a qué aplicarlo en el servidor—.
 */
export function TablaDeConstrucciones({
  filas,
  modo,
  encargado,
  seleccion,
  todasSeleccionadas,
  ocupada,
  onAlternarSeleccion,
  onAlternarTodas,
  onAbrir,
  onValidar,
}: TablaDeConstruccionesProps) {
  const conTerreno = modo !== "oculto";
  const alinear = "text-right";

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead variant="versalitas" className="w-9 pl-5">
            <Checkbox
              aria-label="Seleccionar todas las visibles"
              className={SELECCION_OSCURA}
              checked={todasSeleccionadas}
              onCheckedChange={onAlternarTodas}
            />
          </TableHead>
          <TableHead variant="versalitas">Construcción</TableHead>
          <TableHead variant="versalitas" className={alinear}>Año</TableHead>
          <TableHead variant="versalitas" className={alinear}>Sup. m²</TableHead>
          <TableHead variant="versalitas" className={alinear}>Vida T/R</TableHead>
          <TableHead variant="versalitas" className={alinear}>UF/m² rep · res</TableHead>
          <TableHead variant="versalitas" className={alinear}>Ajuste</TableHead>
          <TableHead variant="versalitas" className={alinear}>Total UF</TableHead>
          {conTerreno ? <TableHead variant="versalitas">Visita en terreno</TableHead> : null}
          <TableHead variant="versalitas" className="text-center">Valid.</TableHead>
          <TableHead className="w-7 pr-3.5">
            <span className="sr-only">Abrir</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {filas.map((f) => {
          const guardada = f.fila !== null;
          const anio =
            f.fila?.anio ?? (f.item ? formularioDesdeElItemDeTerreno(f.item).anio || null : null);
          const ajuste = f.fila?.ajuste_comercial_pct ?? null;
          const ajusteNumero = ajuste === null ? 0 : Number(ajuste);

          return (
            <TableRow
              key={f.clave}
              id={`construccion-${f.clave}`}
              data-state={seleccion.has(f.clave) ? "selected" : undefined}
              onClick={() => onAbrir(f)}
              className="h-10 cursor-pointer"
            >
              <TableCell className="pl-5" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={`Seleccionar ${f.tipo}`}
                  className={SELECCION_OSCURA}
                  checked={seleccion.has(f.clave)}
                  disabled={!guardada}
                  title={guardada ? undefined : "Guárdala primero para poder seleccionarla"}
                  onCheckedChange={() => onAlternarSeleccion(f)}
                />
              </TableCell>

              <TableCell className="max-w-[18.75rem]">
                <div className="flex min-w-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAbrir(f);
                    }}
                    className="text-ink focus-visible:ring-ring/50 min-w-0 truncate rounded-sm text-[0.78125rem] font-bold outline-none focus-visible:ring-[3px]"
                  >
                    {f.tipo}
                  </button>
                  {f.deTerreno ? (
                    <InsigniaTerreno
                      titulo={
                        encargado
                          ? `Creada por ${encargado} en la App de terreno`
                          : "Creada en la App de terreno"
                      }
                    />
                  ) : null}
                </div>
              </TableCell>

              <TableCell className={alinear}>
                <Cifra>{anio ?? "—"}</Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className="font-bold">
                  {f.superficie === null ? "—" : formatearCifra(String(f.superficie))}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className={f.fila?.vida_util_total ? undefined : "text-ink-ghost"}>
                  {formatearVidaUtil(
                    f.fila?.vida_util_total ?? null,
                    f.fila?.vida_util_residual ?? null,
                  )}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra className={f.fila?.valor_reposicion_uf_m2 ? undefined : "text-ink-ghost"}>
                  {f.fila?.valor_reposicion_uf_m2 || f.fila?.valor_residual_uf_m2
                    ? `${formatearCifra(f.fila?.valor_reposicion_uf_m2 ?? null)} · ${formatearCifra(f.fila?.valor_residual_uf_m2 ?? null)}`
                    : "—"}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                <Cifra
                  className={
                    ajusteNumero < 0
                      ? "text-danger-soft-foreground"
                      : ajusteNumero > 0
                        ? "text-success-soft-foreground"
                        : "text-ink-ghost"
                  }
                >
                  {ajusteNumero === 0 ? "—" : formatearAjuste(ajuste)}
                </Cifra>
              </TableCell>
              <TableCell className={alinear}>
                {f.total === null ? (
                  <span className="bg-warning-soft text-warning-soft-foreground rounded-sm px-1.5 py-0.5 text-[0.65625rem] font-bold">
                    Sin valorizar
                  </span>
                ) : (
                  <span className="font-mono text-[0.78125rem] font-bold">
                    {formatearMonto(f.total, null)}
                  </span>
                )}
              </TableCell>

              {conTerreno ? (
                <TableCell>
                  <CeldaDeTerreno fila={f} modo={modo} />
                </TableCell>
              ) : null}

              <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={`Validar ${f.tipo}`}
                  checked={f.validada}
                  disabled={ocupada || !guardada}
                  title={
                    !guardada
                      ? "Guárdala primero para poder validarla"
                      : f.validada
                        ? "Quitar validación"
                        : "Marcar los datos como revisados"
                  }
                  // SE GUARDA SOLA y viaja sin el resto de los campos, para no
                  // pisar lo que otro pudo haber corregido mientras tanto.
                  onCheckedChange={(v) => onValidar(f, v === true)}
                />
              </TableCell>

              <TableCell className="pr-3.5 text-right">
                <ChevronRight className="text-ink-ghost inline size-3.5" aria-hidden="true" />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
